<template>
  <div class="oficina-settings-screen">
    <div class="oficina-settings-container">
      <div class="oficina-settings-header">
        <i class="icofont-settings"></i>
        <h1>Dados da Oficina</h1>
      </div>
      
      <div class="oficina-settings-form">
        <!-- Plataforma -->
        <div class="oficina-form-group">
          <label>Como a oficina vai chegar aos profissionais?</label>
          <div class="oficina-platform-selector">
            <button
              type="button"
              class="oficina-platform-btn"
              :class="{ active: localConfig.platform === 'Google Meet' }"
              @click="updatePlatform('Google Meet')"
            >
              <i class="icofont-monitor"></i> Google Meet
            </button>
            <button
              type="button"
              class="oficina-platform-btn"
              :class="{ active: localConfig.platform === 'Presencial' }"
              @click="updatePlatform('Presencial')"
            >
              <i class="icofont-location-pin"></i> Presencial
            </button>
          </div>
        </div>

        <!-- Facilitador e Ponto Focal -->
        <div class="oficina-form-row">
          <div class="oficina-form-group">
            <label>Eu sou (Facilitador)</label>
            <div class="oficina-input-wrapper">
              <input
                type="text"
                v-model="localConfig.facilitatorName"
                class="oficina-input"
                :class="{ 'oficina-input-error': errors.facilitatorName }"
                placeholder="Seu nome"
                @input="updateConfig"
              />
              <span v-if="loadingFacilitator" class="oficina-loading-spinner">
                <i class="icofont-spinner-alt icofont-spin"></i>
              </span>
            </div>
            <div v-if="errors.facilitatorName" class="oficina-field-error">
              {{ errors.facilitatorName }}
            </div>
          </div>
          <div
            v-if="!isPresencial"
            class="oficina-form-group"
            id="contact-name-group"
          >
            <label>Ponto Focal (Sala)</label>
            <div class="oficina-input-wrapper">
              <input
                type="text"
                v-model="localConfig.contactName"
                class="oficina-input"
                :class="{ 'oficina-input-error': errors.contactName }"
                placeholder="Nome na sala"
                @input="updateConfig"
              />
            </div>
            <div v-if="errors.contactName" class="oficina-field-error">
              {{ errors.contactName }}
            </div>
          </div>
        </div>

        <!-- Link SOS -->
        <div
          v-if="!isPresencial"
          class="oficina-form-group"
          id="sos-link-group"
        >
          <label>Link SOS (Acesso Remoto)</label>
          <input
            type="text"
            v-model="localConfig.sosLink"
            class="oficina-input"
            :class="{ 'oficina-input-error': errors.sosLink }"
            placeholder="Ex: https://remotedesktop.google.com/support"
            @input="updateConfig"
          />
          <div v-if="errors.sosLink" class="oficina-field-error">
            {{ errors.sosLink }}
          </div>
        </div>

        <!-- Estado e Cidade -->
        <div class="oficina-form-row">
          <div class="oficina-form-group">
            <label>Estado</label>
            <vue-select
              v-model="estadoSelecionado"
              :options="estados"
              label="uf"
              :placeholder="dadosRecuperadosDoUsuario && estadoSelecionado ? estadoSelecionado.uf : 'Selecione um estado'"
              class="vue-select"
              :class="{ 'oficina-input-error': errors.state }"
              :disabled="dadosRecuperadosDoUsuario"
              @input="onStateChange"
            >
              <div slot="no-options">Não há opções correspondentes</div>
            </vue-select>
            <div v-if="errors.state" class="oficina-field-error">
              {{ errors.state }}
            </div>
            <div v-if="dadosRecuperadosDoUsuario && estadoSelecionado" class="oficina-field-info">
              <!-- <i class="icofont-info-circle"></i> Estado recuperado automaticamente do seu perfil -->
            </div>
          </div>

          <div class="oficina-form-group">
            <label>Cidade que está recebendo a oficina</label>
            <vue-select
              v-model="cidadeSelecionada"
              :options="cidades"
              label="no_municipio"
              :placeholder="!localConfig.state ? 'Primeiro selecione um estado' : loadingCities ? 'Carregando cidades...' : dadosRecuperadosDoUsuario && cidadeSelecionada ? cidadeSelecionada.no_municipio : 'Selecione uma cidade'"
              class="vue-select"
              :class="{ 'oficina-input-error': errors.city }"
              :disabled="dadosRecuperadosDoUsuario || !localConfig.state || loadingCities"
              :loading="loadingCities"
              @input="onCityChange"
            >
              <template slot="option" slot-scope="option">
                {{ option.no_municipio }}
              </template>
              <div slot="no-options">Não há opções correspondentes</div>
            </vue-select>
            <div v-if="errors.city" class="oficina-field-error">
              {{ errors.city }}
            </div>
            <div v-if="dadosRecuperadosDoUsuario && cidadeSelecionada" class="oficina-field-info">
              <!-- <i class="icofont-info-circle"></i> Cidade recuperada automaticamente do seu perfil -->
            </div>
          </div>
        </div>

        <!-- WhatsApp e Link de Avaliação -->
        <div class="oficina-form-row">
          <div class="oficina-form-group">
            <label>WhatsApp Suporte (Apenas números)</label>
            <input
              type="text"
              v-model="localConfig.whatsappNumber"
              class="oficina-input"
              :class="{ 'oficina-input-error': errors.whatsappNumber }"
              placeholder="5583999999999"
              @input="updateConfig"
            />
            <div v-if="errors.whatsappNumber" class="oficina-field-error">
              {{ errors.whatsappNumber }}
            </div>
          </div>

        </div>

        <!-- Tema -->
        <div class="oficina-form-group oficina-form-group-full">
          <label>Tema da Reunião</label>
          <textarea
            v-model="localConfig.theme"
            class="oficina-textarea"
            :class="{ 'oficina-input-error': errors.theme }"
            placeholder="Ex: apresentar os novos indicadores..."
            @input="updateConfig"
          ></textarea>
          <div v-if="errors.theme" class="oficina-field-error">
            {{ errors.theme }}
          </div>
        </div>

        <button
          type="button"
          class="oficina-btn-primary"
          @click="validateAndStart"
        >
          Iniciar Apresentação <i class="icofont-arrow-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ESTADOS } from '../utils'
import VueSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'SettingsScreen',
  props: {
    config: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localConfig: { ...this.config },
      estados: ESTADOS.map(e => ({ uf: e.value })),
      cidades: [],
      loadingCities: false,
      loadingFacilitator: false,
      errors: {},
      estadoSelecionado: null,
      cidadeSelecionada: null,
      dadosRecuperadosDoUsuario: false, // Flag para indicar se os dados foram recuperados do usuário
    }
  },
  computed: {
    isPresencial() {
      return this.localConfig.platform === 'Presencial'
    },
  },
  watch: {
    config: {
      async handler(newVal) {
        const oldState = this.localConfig.state
        const oldCity = this.localConfig.city
        
        this.localConfig = { ...newVal }

        // Se vier UF "custom" (usuário teste), garantir que ela exista nas opções
        if (newVal.state && !this.estados.find(e => e.uf === newVal.state)) {
          this.estados = [{ uf: newVal.state }, ...this.estados]
        }
        
        // Verificar se os dados foram recuperados do usuário
        if (newVal.state && newVal.city && !this.dadosRecuperadosDoUsuario) {
          this.dadosRecuperadosDoUsuario = true
        }
        
         // Sincronizar estado selecionado se mudou ou se ainda não está selecionado
         if (newVal.state && (newVal.state !== oldState || !this.estadoSelecionado)) {
           // ===== INÍCIO: CÓDIGO PARA TESTES (ESTADO TT) - REMOVER/COMENTAR EM PRODUÇÃO =====
           if (newVal.state === 'TT') {
             // Adicionar TT à lista de estados se não existir
             if (!this.estados.find(e => e.uf === 'TT')) {
               this.estados = [{ uf: 'TT' }, ...this.estados]
             }
             this.estadoSelecionado = { uf: 'TT' }
             
             // Criar lista de cidades customizada para TT
             this.cidades = [
               { no_municipio: 'MUNICIPIO TESTE' }
             ]
             
             // Selecionar a cidade automaticamente
             if (newVal.city) {
               this.cidadeSelecionada = this.cidades.find(c => 
                 c.no_municipio && c.no_municipio.toUpperCase() === newVal.city.toUpperCase()
               ) || this.cidades[0]
               this.localConfig.city = this.cidadeSelecionada.no_municipio
               this.updateConfig()
             }
             return // Não continuar com o fluxo normal
           }
           // ===== FIM: CÓDIGO PARA TESTES (ESTADO TT) =====
           
           const estadoEncontrado = this.estados.find(e => e.uf === newVal.state) || { uf: newVal.state }
           this.estadoSelecionado = estadoEncontrado

           const estadoEhValido = ESTADOS.some(e => e.value === newVal.state)

           // Se UF for válida, carregar cidades da API
           if (estadoEhValido) {
             await this.loadCities(newVal.state)
           } else {
             // UF de teste: não chama API, só cria a "lista" com a cidade atual
             this.cidades = newVal.city ? [{ no_municipio: newVal.city }] : []
             this.cidadeSelecionada = newVal.city ? { no_municipio: newVal.city } : null
             this.localConfig.city = newVal.city || ''
             this.loadingCities = false
             this.updateConfig()
             return
           }
          
          // Após carregar cidades, tentar selecionar a cidade
          if (newVal.city && this.cidades.length > 0) {
            await this.$nextTick()
            const cidadeEncontrada = this.cidades.find(c =>
              c.no_municipio && c.no_municipio.toUpperCase() === newVal.city.toUpperCase()
            )
            if (cidadeEncontrada) {
              this.cidadeSelecionada = cidadeEncontrada
              this.localConfig.city = cidadeEncontrada.no_municipio
              this.updateConfig()
            } else {
              // Tentar busca parcial
              const cidadeParcial = this.cidades.find(c =>
                c.no_municipio && c.no_municipio.toUpperCase().includes(newVal.city.toUpperCase())
              )
              if (cidadeParcial) {
                this.cidadeSelecionada = cidadeParcial
                this.localConfig.city = cidadeParcial.no_municipio
                this.updateConfig()
              }
            }
          }
        }
        
        // Sincronizar cidade selecionada se mudou e já temos cidades carregadas (mas estado não mudou)
        if (newVal.city && newVal.city !== oldCity && this.cidades.length > 0 && newVal.state === oldState) {
          const cidadeEncontrada = this.cidades.find(c => 
            c.no_municipio && c.no_municipio.toUpperCase() === newVal.city.toUpperCase()
          )
          if (cidadeEncontrada) {
            this.cidadeSelecionada = cidadeEncontrada
          }
        }
      },
      deep: true,
      immediate: true, // Executar imediatamente quando o componente é criado
    },
  },
  async mounted() {
    
    // Verificar se os dados foram recuperados do usuário logado
    if (this.localConfig.state && this.localConfig.city) {
      this.dadosRecuperadosDoUsuario = true
    }
    
    // Inicializar estado selecionado se já tiver valor
    if (this.localConfig.state) {
      // ===== INÍCIO: CÓDIGO PARA TESTES (ESTADO TT) - REMOVER/COMENTAR EM PRODUÇÃO =====
      if (this.localConfig.state === 'TT') {
        // Adicionar TT à lista de estados
        if (!this.estados.find(e => e.uf === 'TT')) {
          this.estados = [{ uf: 'TT' }, ...this.estados]
        }
        this.estadoSelecionado = { uf: 'TT' }
        
        // Criar lista de cidades customizada para TT32
        this.cidades = [ 
          { no_municipio: 'MUNICIPIO TESTE' }
        ]
        
        // Selecionar a cidade automaticamente
        if (this.localConfig.city) {
          this.cidadeSelecionada = this.cidades.find(c => 
            c.no_municipio && c.no_municipio.toUpperCase() === this.localConfig.city.toUpperCase()
          ) || this.cidades[0]
          this.localConfig.city = this.cidadeSelecionada.no_municipio
          this.updateConfig()
        }
        return // Não continuar com o fluxo normal
      }
      // ===== FIM: CÓDIGO PARA TESTES (ESTADO TT) =====
      
      // Buscar estado na lista normal
      this.estadoSelecionado = this.estados.find(e => e.uf === this.localConfig.state)
      
      if (this.estadoSelecionado) {
        await this.loadCities(this.localConfig.state)
        
        // Após carregar cidades, selecionar a cidade automaticamente
        if (this.localConfig.city && this.cidades.length > 0) {
          this.cidadeSelecionada = this.cidades.find(c => 
            c.no_municipio && c.no_municipio.toUpperCase() === this.localConfig.city.toUpperCase()
          )
          if (this.cidadeSelecionada) {
            // Atualizar o config com a cidade encontrada
            this.localConfig.city = this.cidadeSelecionada.no_municipio
            this.updateConfig()
          } else {
            console.warn('Cidade não encontrada nas cidades carregadas:', this.localConfig.city)
            // Tentar buscar por nome parcial
            const cidadeParcial = this.cidades.find(c => 
              c.no_municipio && c.no_municipio.toUpperCase().includes(this.localConfig.city.toUpperCase())
            )
            if (cidadeParcial) {
              this.cidadeSelecionada = cidadeParcial
              this.localConfig.city = cidadeParcial.no_municipio
              this.updateConfig()
            }
          }
        }
      } else {
        console.warn('Estado não encontrado nos estados disponíveis:', this.localConfig.state)
      }
    } else {
      console.log('Nenhum estado configurado - usuário poderá selecionar')
    }
  },
  methods: {
    updatePlatform(platform) {
      this.localConfig.platform = platform
      this.updateConfig()
    },
    async onStateChange(estado) {
      if (estado && estado.uf) {
        this.localConfig.state = estado.uf
        this.estadoSelecionado = estado
        await this.loadCities(estado.uf)
      } else {
        this.localConfig.state = ''
        this.estadoSelecionado = null
        this.cidades = []
        this.localConfig.city = ''
        this.cidadeSelecionada = null
      }
      this.updateConfig()
    },
    onCityChange(cidade) {
      if (cidade && cidade.no_municipio) {
        this.localConfig.city = cidade.no_municipio
        this.cidadeSelecionada = cidade
      } else {
        this.localConfig.city = ''
        this.cidadeSelecionada = null
      }
      this.updateConfig()
    },
    async loadCities(uf) {
      if (!uf) return
      
      this.loadingCities = true
      this.cidades = []
      
      try {
        const response = await this.$apiPublic.get(`/cidades-por-uf/${uf}`)
        this.cidades = response.data || []
        
        // Se havia uma cidade selecionada antes, tentar restaurar
        if (this.localConfig.city) {
          const cidadeEncontrada = this.cidades.find(
            (c) => c.no_municipio && c.no_municipio.toUpperCase() === this.localConfig.city.toUpperCase()
          )
          if (cidadeEncontrada) {
            this.localConfig.city = cidadeEncontrada.no_municipio
            this.cidadeSelecionada = cidadeEncontrada
          } else {
            // Se não encontrou, limpar seleção
            console.warn('Cidade não encontrada na lista de cidades do estado:', this.localConfig.city)
            this.localConfig.city = ''
            this.cidadeSelecionada = null
          }
        }
      } catch (error) {
        console.error('Erro ao carregar cidades:', error)
        this.$toast.error('Erro ao carregar cidades', 'Erro', {
          position: 'topRight',
        })
      } finally {
        this.loadingCities = false
      }
    },
    updateConfig() {
      this.$emit('update-config', { ...this.localConfig })
      // Limpar erros ao atualizar
      this.errors = {}
    },
    validateAndStart() {
      // Limpar erros anteriores
      this.errors = {}
      
      const errors = {}
      
      // Validar Facilitador
      if (!this.localConfig.facilitatorName || this.localConfig.facilitatorName.trim() === '') {
        errors.facilitatorName = 'O nome do facilitador é obrigatório'
      }
      
      // Validar Ponto Focal (apenas se não for presencial)
      if (!this.isPresencial && (!this.localConfig.contactName || this.localConfig.contactName.trim() === '')) {
        errors.contactName = 'O nome do ponto focal é obrigatório'
      }
      
      // Validar Estado
      if (!this.localConfig.state || this.localConfig.state === '' || !this.estadoSelecionado) {
        errors.state = 'Selecione um estado'
      }
      
      // Validar Cidade
      if (!this.localConfig.city || this.localConfig.city === '' || !this.cidadeSelecionada) {
        errors.city = 'Selecione uma cidade'
      }
      
      // Validar WhatsApp
      if (!this.localConfig.whatsappNumber || this.localConfig.whatsappNumber.trim() === '') {
        errors.whatsappNumber = 'O número do WhatsApp é obrigatório'
      }
      
      // Validar Tema
      if (!this.localConfig.theme || this.localConfig.theme.trim() === '') {
        errors.theme = 'O tema da reunião é obrigatório'
      }
      
      // Validar Link SOS (apenas se não for presencial)
      if (!this.isPresencial && (!this.localConfig.sosLink || this.localConfig.sosLink.trim() === '')) {
        errors.sosLink = 'O link SOS é obrigatório'
      }
      
      // Validar Plataforma
      if (!this.localConfig.platform || this.localConfig.platform.trim() === '') {
        errors.platform = 'Selecione uma plataforma'
      }
      
      // Se houver erros, exibir e não prosseguir
      if (Object.keys(errors).length > 0) {
        this.errors = errors
        
        // Scroll para o primeiro erro
        this.$nextTick(() => {
          const firstError = document.querySelector('.oficina-input-error, .vue-select.oficina-input-error')
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
            // Tentar focar no vue-select se for o caso
            const vueSelect = firstError.querySelector('.vs__search')
            if (vueSelect) {
              vueSelect.focus()
            }
          }
        })
        
        return
      }
      
      // Se tudo estiver válido, emitir evento para iniciar apresentação
      this.$emit('start-presentation')
    },
  },
}
</script>

<style scoped>
/* Estilos serão adicionados em arquivo CSS separado */
</style>
